import type { UIMatch } from 'react-router';

const ZENDESK_SCRIPT_ORIGIN = 'https://static.zdassets.com';
const ZENDESK_SCRIPT_PATH = '/ekr/snippet.js';
const ZENDESK_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HIDDEN_ROUTE_IDS = ['routes/_app.cart', 'routes/_checkout', 'routes/_app.p.$'];

type ZendeskWebWidgetPreferences = Pick<
    SitePreferencesCustomAttributes,
    'zendeskWebWidgetScriptUrl' | 'zendeskWebWidgetSettings'
>;

export type ZendeskWebWidgetConfig = {
    scriptUrl: string;
    settings: Record<string, unknown> | null;
};

declare global {
    interface Window {
        zE?: (product: 'webWidget', command: 'hide' | 'show') => void;
        zESettings?: Record<string, unknown>;
    }
}

function parseSettings(rawSettings: string | undefined): Record<string, unknown> | null {
    if (!rawSettings?.trim()) {
        return null;
    }

    try {
        const settings: unknown = JSON.parse(rawSettings);
        if (typeof settings === 'object' && settings !== null && !Array.isArray(settings)) {
            return settings as Record<string, unknown>;
        }
    } catch {
        // Invalid optional settings must not prevent the support widget from loading.
    }

    return null;
}

export function resolveZendeskWebWidgetConfig(preferences: ZendeskWebWidgetPreferences): ZendeskWebWidgetConfig | null {
    const configuredScriptUrl = preferences.zendeskWebWidgetScriptUrl?.trim();
    if (!configuredScriptUrl) {
        return null;
    }

    let scriptUrl: URL;
    try {
        scriptUrl = new URL(configuredScriptUrl);
    } catch {
        return null;
    }

    const keys = scriptUrl.searchParams.getAll('key');
    if (
        scriptUrl.origin !== ZENDESK_SCRIPT_ORIGIN ||
        scriptUrl.pathname !== ZENDESK_SCRIPT_PATH ||
        scriptUrl.username ||
        scriptUrl.password ||
        scriptUrl.hash ||
        scriptUrl.searchParams.size !== 1 ||
        keys.length !== 1 ||
        !ZENDESK_KEY_PATTERN.test(keys[0] ?? '')
    ) {
        return null;
    }

    return {
        scriptUrl: scriptUrl.toString(),
        settings: parseSettings(preferences.zendeskWebWidgetSettings),
    };
}

export function serializeZendeskWebWidgetSettings(settings: Record<string, unknown>): string {
    return `window.zESettings=${JSON.stringify(settings)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029')};`;
}

export function shouldHideZendeskWebWidget(matches: Pick<UIMatch, 'id'>[]): boolean {
    return matches.some((match) => HIDDEN_ROUTE_IDS.some((routeId) => match.id.includes(routeId)));
}

export function syncZendeskWebWidgetVisibility(hidden: boolean): boolean {
    if (typeof window === 'undefined' || !window.zE) {
        return false;
    }

    window.zE('webWidget', hidden ? 'hide' : 'show');
    return true;
}
